import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Palette, Share2, Sparkles, ArrowRight } from "lucide-react";

/**
 * TenantOnboardingTour — a one-time welcome modal that walks a fresh
 * white-label buyer through the 3 steps to make their site theirs.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()  — called when user dismisses or completes intro
 *  - onSkip ()   — user chose "Skip tour" (backend marks all steps done)
 *  - brandName   — buyer's business name for personalization
 */
export default function TenantOnboardingTour({ open, onClose, onSkip, brandName = "your business" }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const steps = [
    {
      icon: Upload,
      title: "Upload your logo",
      body: "Drop in a PNG, SVG, or JPG — it'll appear in your header, on receipts, and in every client-facing email.",
    },
    {
      icon: Palette,
      title: "Set your brand color",
      body: "Pick the color that shows up on buttons, links, and the accent border across your entire site.",
    },
    {
      icon: Share2,
      title: "Share your URL",
      body: "Copy your public site link and drop it in your bio, on business cards, or send it straight to your first client.",
    },
  ];

  const current = steps[step];
  const Icon = current.icon;
  const isLast = step === steps.length - 1;

  const goNext = () => {
    if (isLast) {
      onClose?.();
      navigate("/tenant/settings");
    } else {
      setStep(step + 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="tenant-onboarding-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary mb-1">
            <Sparkles size={14} />
            <span className="label-caps text-[10px]">Welcome to {brandName}</span>
          </div>
          <DialogTitle className="font-serif text-3xl leading-tight">
            Let's get you live in 3 steps.
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground pt-1">
            You just bought a fully functional tax practice site. Give it your look, then send clients your way.
          </DialogDescription>
        </DialogHeader>

        {/* Progress dots */}
        <div className="flex items-center gap-2 pt-2" data-testid="tenant-onboarding-progress">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-secondary"}`}
            />
          ))}
        </div>

        {/* Active step card */}
        <div className="rounded-lg border border-border bg-secondary/40 p-6 flex gap-4">
          <div className="h-12 w-12 rounded-md bg-primary/10 text-primary grid place-items-center flex-shrink-0">
            <Icon size={22} />
          </div>
          <div>
            <div className="label-caps text-[10px] mb-1">Step {step + 1} of {steps.length}</div>
            <div className="font-serif text-2xl mb-2 leading-tight">{current.title}</div>
            <p className="text-sm text-muted-foreground">{current.body}</p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onSkip}
            data-testid="tenant-onboarding-skip"
          >
            Skip tour
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" size="sm" onClick={() => setStep(step - 1)}>Back</Button>
            )}
            <Button size="sm" onClick={goNext} data-testid="tenant-onboarding-next">
              {isLast ? "Take me there" : "Next"} <ArrowRight size={12} className="ml-1" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
